import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Select,
  SkeletonRows,
  StatTile,
  Toolbar,
  formatMoney,
} from '@biovet/ui';
import { auth } from '../lib/actor.js';
import { Th } from './parts.js';
import { Messages, Tiles, useAction } from './document.js';
import {
  apiSend,
  useResource,
  type AdminRefs,
  type AdminRole,
  type AdminUser,
  type AdminWriteResult,
  type AdminZone,
  type GeographyRefs,
  type RolesResponse,
} from '../lib/api.js';


type Focus = 'hierarchy' | 'roles' | 'zones';

export function UsersZonesScreen({ focus }: { focus: Focus }) {
  if (focus === 'roles') return <RolesScreen />;
  if (focus === 'zones') return <ZonesScreen />;
  return <HierarchyScreen />;
}


interface UserDraft {
  mode: 'create' | 'edit';
  id?: string;
  username: string;
  email: string;
  fullName: string;
  initials: string;
  jobTitle: string;
  phone: string;
  roleCode: string;
  status: string;
  scopeLevel: string;
  scopeCode: string;
  branchCode: string;
}

function HierarchyScreen() {
  const [includeInactive, setIncludeInactive] = useState(false);
  const usersRes = useResource<{ users: AdminUser[] }>('/admin/users', {
    ...auth(),
    include: includeInactive ? 'inactive' : undefined,
  });
  const refsRes = useResource<AdminRefs>('/admin/refs', auth());

  const users = useMemo(() => usersRes.data?.users ?? [], [usersRes.data]);
  const refs = refsRes.data;
  const caller = refs?.caller;
  const { busy, notice, problem, run } = useAction(usersRes.reload);

  const [draft, setDraft] = useState<UserDraft | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const current = users.find((u) => u.id === selected);

  const [issued, setIssued] = useState<{ username: string; password: string } | null>(null);

  useEffect(() => {
    if (selected && !users.some((u) => u.id === selected)) setSelected(null);
  }, [users, selected]);

  const save = (d: UserDraft) => {
    const payload = {
      username: d.username,
      email: d.email,
      fullName: d.fullName,
      initials: d.initials || null,
      jobTitle: d.jobTitle || null,
      phone: d.phone || null,
      roleCode: d.roleCode,
      scopeLevel: d.scopeLevel,
      scopeCode: d.scopeLevel === 'COMPANY' ? null : d.scopeCode || null,
      branchCode: d.branchCode || null,
    };
    void run(async () => {
      const result =
        d.mode === 'create'
          ? await apiSend<AdminWriteResult>('POST', '/admin/users', payload, auth())
          : await apiSend<AdminWriteResult>(
              'PATCH',
              `/admin/users/${d.id}`,
              { ...payload, username: undefined, email: undefined, status: d.status },
              auth(),
            );
      setDraft(null);
      if (result.oneTimePassword && result.username) {
        setIssued({ username: result.username, password: result.oneTimePassword });
      }
      return result;
    });
  };

  const resetPassword = (user: AdminUser) => {
    void run(async () => {
      const result = await apiSend<AdminWriteResult>(
        'POST',
        `/admin/users/${user.id}/password`,
        {},
        auth(),
      );
      if (result.oneTimePassword) {
        setIssued({ username: user.username, password: result.oneTimePassword });
      }
      return result;
    });
  };

  const active = users.filter((u) => u.status === 'ACTIVE').length;

  return (
    <>
      <PageHeader
        breadcrumb="Users & Zones / User Hierarchy"
        title="User hierarchy"
        description="Who works here, and where each person sits in the company."
      />

      <Messages notice={notice} problem={problem} />

      {issued && (
        <div className="pb-3.5">
          <Callout tone="warn" title="Hand this over now — it cannot be shown again">
            <p className="mt-0 mb-3">
              Only a hash of it is stored, so there is no screen that can repeat it. If it is lost,
              issue another one.
            </p>
            <div className="flex flex-wrap items-center gap-4 rounded-[var(--radius)] border border-[var(--line)] bg-[var(--surface-2)] p-3">
              <div className="flex flex-col">
                <span className="bb-eyebrow">User ID</span>
                <span className="bb-mono text-[14px] font-semibold">{issued.username}</span>
              </div>
              <div className="flex flex-col">
                <span className="bb-eyebrow">One-time password</span>
                <span className="bb-mono text-[15px] font-semibold tracking-wider">
                  {issued.password}
                </span>
              </div>
              <div className="flex-1" />
              <Button
                size="sm"
                variant="secondary"
                onClick={() =>
                  void navigator.clipboard?.writeText(
                    `User ID: ${issued.username}\nPassword: ${issued.password}`,
                  )
                }
              >
                Copy
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setIssued(null)}>
                I have handed it over
              </Button>
            </div>
            <p className="mt-3 mb-0 text-[11.5px]">
              They sign in with this once and are made to choose their own before they can reach any
              screen. Until they do, every route but the password form is refused.
            </p>
          </Callout>
        </div>
      )}

      {}

      <Tiles>
        <Card>
          <StatTile
            label="Accounts you administer"
            value={String(users.length)}
            hint={`${active} active`}
          />
        </Card>
        <Card>
          <StatTile
            label="Roles in play"
            value={String(new Set(users.map((u) => u.roleCode)).size)}
            hint="distinct across the directory"
          />
        </Card>
        <Card>
          <StatTile
            label="Company-wide scope"
            value={String(users.filter((u) => u.scopeLevel === 'COMPANY').length)}
            hint="see every zone and branch"
          />
        </Card>
        <Card>
          <StatTile
            label="Never signed in"
            value={String(users.filter((u) => !u.lastLoginAt).length)}
            hint="issued a password, not yet used"
          />
        </Card>
      </Tiles>

      {draft && refs && (
        <UserForm
          draft={draft}
          refs={refs}
          busy={busy}
          onChange={setDraft}
          onSave={() => save(draft)}
          onCancel={() => setDraft(null)}
        />
      )}

      <Card flush>
        <CardHeader
          title="Directory"
          subtitle="Indented by authority — a role that ranks lower sits further left"
          actions={
            <Toolbar>
              <Button
                size="sm"
                variant={includeInactive ? 'secondary' : 'ghost'}
                onClick={() => setIncludeInactive((v) => !v)}
              >
                Show disabled
              </Button>
              <Button
                size="sm"
                variant="primary"
                disabled={!caller?.canCreateUser || !refs}
                onClick={() => setDraft(blankUser(refs))}
              >
                New user
              </Button>
            </Toolbar>
          }
        />
        {usersRes.loading ? (
          <SkeletonRows rows={6} />
        ) : usersRes.error ? (
          <CardBody>
            <Callout tone="danger" title="That did not load">
              {usersRes.error}
            </Callout>
          </CardBody>
        ) : users.length === 0 ? (
          <EmptyState title="Nobody here" description="There are no accounts for you to manage." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] border-collapse">
              <thead>
                <tr className="bg-[var(--surface-2)]">
                  <Th>Name</Th>
                  <Th width="180px">Role</Th>
                  <Th width="200px">Data scope</Th>
                  <Th width="120px">Home branch</Th>
                  <Th align="right" width="150px">
                    Approval limit
                  </Th>
                  <Th align="right" width="110px">
                    Status
                  </Th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr
                    key={u.id}
                    onClick={() => setSelected(u.id === selected ? null : u.id)}
                    className={`bb-row-h cursor-pointer border-b border-[var(--line-2)] hover:bg-[var(--surface-2)] ${
                      u.id === selected ? 'bg-[var(--surface-2)]' : ''
                    }`}
                  >
                    <td className="px-[var(--pad)] text-[13px]">
                      {}
                      <span
                        style={{ paddingLeft: Math.min(u.rank / 10, 6) * 18 }}
                        className="inline-flex items-center gap-2"
                      >
                        <span className={u.rank === 0 ? 'font-semibold' : 'font-medium'}>
                          {u.fullName}
                        </span>
                        <span className="bb-mono text-[11.5px] text-[var(--ink-3)]">{u.email}</span>
                      </span>
                    </td>
                    <td className="px-2.5 text-[12.5px] text-[var(--ink-2)]">
                      {u.roleName}
                      {u.inheritsScope && (
                        <span className="pl-2 text-[11px] text-[var(--ink-3)]">inherits below</span>
                      )}
                    </td>
                    <td className="px-2.5 text-[12.5px] text-[var(--ink-2)]">
                      {u.scopeLevel === 'COMPANY'
                        ? 'Whole company'
                        : `${u.scopeLevel[0]}${u.scopeLevel.slice(1).toLowerCase()} · ${u.scopeName}`}
                    </td>
                    <td className="px-2.5 text-[12.5px] text-[var(--ink-2)]">
                      {u.branchCode ?? '—'}
                    </td>
                    <td className="bb-num px-2.5 text-right text-[12.5px]">
                      {u.approvalLimitBase === null
                        ? 'Unlimited'
                        : formatMoney(u.approvalLimitBase, { decimals: 0 })}
                    </td>
                    <td className="px-[var(--pad)] text-right">
                      <Badge
                        tone={
                          u.status === 'ACTIVE' ? 'ok' : u.status === 'INVITED' ? 'info' : 'mute'
                        }
                      >
                        {u.status}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {current && !draft && (
        <Card flush className="mt-3.5" data-noprint="1">
          <CardHeader
            title={current.fullName}
            subtitle={`${current.roleName} · ${current.permissionCount} permissions · ${current.lastLoginAt ? `last signed in ${current.lastLoginAt.slice(0, 10)}` : 'has never signed in'}`}
            actions={
              <Toolbar>
                <Button
                  size="sm"
                  disabled={!caller?.canEditUser || !current.canManage}
                  onClick={() => setDraft(editUser(current))}
                >
                  Edit
                </Button>
                {}
                <Button
                  size="sm"
                  variant="secondary"
                  loading={busy}
                  disabled={!caller?.canEditUser || !current.canManage}
                  onClick={() => resetPassword(current)}
                  title="Generate a one-time password and sign this account out everywhere"
                >
                  Reset password
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  loading={busy}
                  disabled={!caller?.canDeleteUser || !current.canManage || current.isSuperAdmin}
                  onClick={() =>
                    void run(async () => {
                      const result = await apiSend<AdminWriteResult>(
                        'DELETE',
                        `/admin/users/${current.id}`,
                        undefined,
                        auth(),
                      );
                      setSelected(null);
                      return result;
                    })
                  }
                >
                  Remove
                </Button>
              </Toolbar>
            }
          />
          <CardBody className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <Fact label="User ID" value={current.username} mono />
            <Fact label="Email" value={current.email} mono />
            <Fact label="Job title" value={current.jobTitle ?? '—'} />
            <Fact label="Phone" value={current.phone ?? '—'} mono />
            <Fact label="Authority rank" value={String(current.rank)} mono />
            <Fact
              label="Reaches"
              value={
                current.inheritsScope
                  ? `${current.reportCount} lesser role${current.reportCount === 1 ? '' : 's'}`
                  : 'own scope only'
              }
            />
            <Fact
              label="Self-approve"
              value={current.approvalLimitBase === null ? 'Unlimited' : 'Limited'}
            />
            <Fact label="Scope" value={current.scopeName} />
          </CardBody>
          <CardBody className="border-t border-[var(--line-2)] pt-3">
            {current.isSuperAdmin ? (
              <Callout tone="warn" title="The super administrator cannot be removed">
                This account carries <code className="bb-mono">is_superadmin</code>, which bypasses
                every company grant — it is the one route back into a book whose permissions have
                locked everybody out. Neither <strong>Remove</strong> nor switching it to DISABLED
                is accepted, and the refusal is the API&rsquo;s, not this screen&rsquo;s. The flag
                is set by <code className="bb-mono">seed/0007_single_user.sql</code> and is not
                assignable through any endpoint at any rank, so clearing it is a deliberate SQL
                step. Everything else about the account stays editable.
              </Callout>
            ) : (
              <Callout tone="info" title="Removing an account is not always a delete">
                An account that has created or posted anything is <strong>disabled</strong> rather
                than deleted — every voucher names whoever entered it, and the audit trail has to go
                on being able to. One that has never touched the book is deleted outright. The
                server decides which and says so.
              </Callout>
            )}
          </CardBody>
        </Card>
      )}
    </>
  );
}

function blankUser(refs: AdminRefs | undefined): UserDraft {
  const weakest = refs?.roles[refs.roles.length - 1];
  return {
    mode: 'create',
    username: '',
    email: '',
    fullName: '',
    initials: '',
    jobTitle: '',
    phone: '',
    roleCode: weakest?.code ?? '',
    status: 'INVITED',
    scopeLevel: 'COMPANY',
    scopeCode: '',
    branchCode: refs?.branches.find((b) => b.isHeadOffice)?.code ?? '',
  };
}

function editUser(user: AdminUser): UserDraft {
  return {
    mode: 'edit',
    id: user.id,
    username: user.username,
    email: user.email,
    fullName: user.fullName,
    initials: user.initials ?? '',
    jobTitle: user.jobTitle ?? '',
    phone: user.phone ?? '',
    roleCode: user.roleCode,
    status: user.status,
    scopeLevel: user.scopeLevel,
    scopeCode: user.scopeCode ?? '',
    branchCode: user.branchCode ?? '',
  };
}

function UserForm({
  draft,
  refs,
  busy,
  onChange,
  onSave,
  onCancel,
}: {
  draft: UserDraft;
  refs: AdminRefs;
  busy: boolean;
  onChange: (draft: UserDraft) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  const set = (patch: Partial<UserDraft>) => onChange({ ...draft, ...patch });
  const creating = draft.mode === 'create';

  const scopeOptions =
    draft.scopeLevel === 'ZONE'
      ? refs.zones.map((z) => ({
          value: z.code,
          label: `${'— '.repeat(z.depth)}${z.code} · ${z.name}`,
        }))
      : draft.scopeLevel === 'BRANCH'
        ? refs.branches.map((b) => ({ value: b.code, label: `${b.code} · ${b.name}` }))
        : draft.scopeLevel === 'WAREHOUSE'
          ? refs.warehouses.map((w) => ({ value: w.code, label: `${w.code} · ${w.name}` }))
          : [];

  const ready =
    draft.fullName.trim() !== '' &&
    draft.roleCode !== '' &&
    (!creating || (draft.username.trim() !== '' && draft.email.trim() !== '')) &&
    (draft.scopeLevel === 'COMPANY' || draft.scopeCode !== '');

  return (
    <Card flush className="mb-3.5" data-noprint="1">
      <CardHeader
        title={creating ? 'New user' : `Edit ${draft.fullName}`}
        subtitle={
          creating
            ? 'A one-time password is generated and shown once — hand it over, and they choose their own'
            : 'User ID and email are identity and stay fixed — the audit trail names this account'
        }
        actions={
          <Toolbar>
            <Button size="sm" onClick={onCancel}>
              Cancel
            </Button>
            <Button size="sm" variant="primary" loading={busy} disabled={!ready} onClick={onSave}>
              {creating ? 'Create' : 'Save'}
            </Button>
          </Toolbar>
        }
      />
      <CardBody className="flex flex-col gap-3.5">
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Field label="Full name" required>
            <Input value={draft.fullName} onChange={(e) => set({ fullName: e.target.value })} />
          </Field>
          <Field label="User ID" required={creating} hint={creating ? 'Used to sign in' : 'Fixed'}>
            <Input
              value={draft.username}
              disabled={!creating}
              onChange={(e) => set({ username: e.target.value })}
            />
          </Field>
          <Field label="Email" required={creating} hint={creating ? undefined : 'Fixed'}>
            <Input
              type="text"
              value={draft.email}
              disabled={!creating}
              onChange={(e) => set({ email: e.target.value })}
            />
          </Field>
          <Field label="Initials" hint="Avatar fallback — derived if left blank">
            <Input value={draft.initials} onChange={(e) => set({ initials: e.target.value })} />
          </Field>
          <Field label="Job title">
            <Input value={draft.jobTitle} onChange={(e) => set({ jobTitle: e.target.value })} />
          </Field>
          <Field label="Phone">
            <Input value={draft.phone} onChange={(e) => set({ phone: e.target.value })} />
          </Field>
          {}
          <Field label="Role" required hint="The roles you may assign">
            <Select value={draft.roleCode} onChange={(e) => set({ roleCode: e.target.value })}>
              {refs.roles.map((r) => (
                <option key={r.code} value={r.code}>
                  {r.name}
                </option>
              ))}
            </Select>
          </Field>
          {!creating && (
            <Field label="Status">
              <Select value={draft.status} onChange={(e) => set({ status: e.target.value })}>
                {refs.statuses.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          <Field label="Scope level" hint="How far this account's data reaches">
            <Select
              value={draft.scopeLevel}
              onChange={(e) => set({ scopeLevel: e.target.value, scopeCode: '' })}
            >
              {refs.scopeLevels.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </Select>
          </Field>
          {draft.scopeLevel !== 'COMPANY' && (
            <Field label={`Which ${draft.scopeLevel.toLowerCase()}`} required>
              <Select value={draft.scopeCode} onChange={(e) => set({ scopeCode: e.target.value })}>
                <option value="">—</option>
                {scopeOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          <Field label="Home branch" hint="What the top bar opens on">
            <Select value={draft.branchCode} onChange={(e) => set({ branchCode: e.target.value })}>
              <option value="">—</option>
              {refs.branches.map((b) => (
                <option key={b.code} value={b.code}>
                  {b.code} · {b.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        {creating && (
          <Callout tone="info" title="The password is generated, not chosen here">
            There is no field for it on purpose. An administrator who picks another person&rsquo;s
            password tends to pick one they can remember — which is to say one they will still know
            afterwards, and every entry in this book is signed with the name of whoever made it.
            Saving shows a generated password once, to read out or hand over; it is stored only as a
            hash, so no screen can repeat it. The holder is made to replace it before they can reach
            anything, and until they do the server refuses every route but the password form.
          </Callout>
        )}
      </CardBody>
    </Card>
  );
}


function RolesScreen() {
  const rolesRes = useResource<RolesResponse>('/admin/roles', auth());
  const refsRes = useResource<AdminRefs>('/admin/refs', auth());
  const data = rolesRes.data;
  const caller = refsRes.data?.caller;
  const { busy, notice, problem, run } = useAction(rolesRes.reload);

  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const [matrix, setMatrix] = useState<Set<string> | null>(null);
  const [group, setGroup] = useState<string>('all');
  const [draft, setDraft] = useState<RoleDraft | null>(null);

  const roles = data?.roles ?? [];
  const selected = roles.find((r) => r.code === selectedCode) ?? roles[0];

  const create = (d: RoleDraft) => {
    const roleCode = d.code.trim().toUpperCase();
    void run(async () => {
      const result = await apiSend<AdminWriteResult>(
        'POST',
        '/admin/roles',
        {
          code: roleCode,
          name: d.name.trim(),
          description: d.description.trim() || null,
          rank: Number(d.rank),
          inheritsSubordinateScope: d.inheritsSubordinateScope,
          canSelfApprove: d.canSelfApprove,
          approvalLimitBase: d.approvalLimitBase.trim() || null,
          permissions: [],
        },
        auth(),
      );
      setDraft(null);
      setSelectedCode(roleCode);
      return result;
    });
  };

  useEffect(() => {
    if (!selected || !data) return;
    setMatrix(new Set(data.grants[selected.code] ?? []));
  }, [selected, data]);

  const groups = useMemo(
    () => ['all', ...new Set((data?.modules ?? []).map((m) => m.groupName))],
    [data],
  );
  const modules = useMemo(
    () => (data?.modules ?? []).filter((m) => group === 'all' || m.groupName === group),
    [data, group],
  );

  const dirty = useMemo(() => {
    if (!matrix || !selected || !data) return false;
    const saved = new Set(data.grants[selected.code] ?? []);
    if (saved.size !== matrix.size) return true;
    for (const p of matrix) if (!saved.has(p)) return true;
    return false;
  }, [matrix, selected, data]);

  const toggle = (module: string, action: string) => {
    if (!matrix) return;
    const next = new Set(matrix);
    const key = `${module}:${action}`;
    const held = next.has(key);

    if (action === 'VIEW' && held) {
      for (const perm of [...next]) if (perm.startsWith(`${module}:`)) next.delete(perm);
    } else if (held) {
      next.delete(key);
    } else {
      next.add(key);
      next.add(`${module}:VIEW`);
    }
    setMatrix(next);
  };

  const grouped = useMemo(() => {
    const out: Array<{ name: string; modules: typeof modules }> = [];
    for (const m of modules) {
      const last = out[out.length - 1];
      if (last && last.name === m.groupName) last.modules.push(m);
      else out.push({ name: m.groupName, modules: [m] });
    }
    return out;
  }, [modules]);

  const setAll = (rows: typeof modules, on: boolean, action?: string) => {
    if (!matrix) return;
    const next = new Set(matrix);
    for (const m of rows) {
      for (const a of m.allowedActions) {
        if (action && a !== action) continue;
        if (on) next.add(`${m.code}:${a}`);
        else next.delete(`${m.code}:${a}`);
      }
    }
    setMatrix(next);
  };

  const coverage = (rows: typeof modules, action?: string) => {
    let held = 0;
    let total = 0;
    for (const m of rows) {
      for (const a of m.allowedActions) {
        if (action && a !== action) continue;
        total += 1;
        if (matrix?.has(`${m.code}:${a}`)) held += 1;
      }
    }
    return { all: total > 0 && held === total, partial: held > 0 && held < total, total };
  };

  const editable = Boolean(caller?.canEditRole) && Boolean(selected?.canManage);

  return (
    <>
      <PageHeader
        breadcrumb="Users & Zones / Roles & Permissions"
        title="Roles & permissions"
        description="Decide what each role is allowed to see and do."
      />

      <Messages notice={notice} problem={problem} />

      {}

      {draft && (
        <RoleForm
          draft={draft}
          callerRank={data?.callerRank ?? 0}
          callerRoleName={data?.callerRoleName ?? ''}
          takenCodes={roles.map((r) => r.code)}
          busy={busy}
          onChange={setDraft}
          onSave={() => create(draft)}
          onCancel={() => setDraft(null)}
        />
      )}

      <div className="grid gap-3.5 pt-0 xl:grid-cols-[340px_1fr]">
        <Card flush>
          <CardHeader
            title="Roles"
            subtitle="Ordered by authority"
            actions={
              <Button
                size="sm"
                variant="primary"
                disabled={!caller?.canCreateRole || !data}
                onClick={() => setDraft(blankRole(roles, data?.callerRank ?? 0))}
              >
                New role
              </Button>
            }
          />
          {rolesRes.loading ? (
            <SkeletonRows rows={5} />
          ) : rolesRes.error ? (
            <CardBody>
              <Callout tone="danger" title="That did not load">
                {rolesRes.error}
              </Callout>
            </CardBody>
          ) : (
            <CardBody className="flex flex-col gap-2">
              {roles.map((r) => (
                <button
                  key={r.code}
                  type="button"
                  onClick={() => setSelectedCode(r.code)}
                  className={`rounded border px-3 py-2 text-left transition ${
                    r.code === selected?.code
                      ? 'border-[var(--accent)] bg-[var(--surface-2)]'
                      : 'border-[var(--line-2)] hover:bg-[var(--surface-2)]'
                  }`}
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-[13px] font-medium">{r.name}</span>
                    <span className="bb-num text-[11px] text-[var(--ink-3)]">rank {r.rank}</span>
                  </div>
                  <div className="pt-0.5 text-[11.5px] text-[var(--ink-3)]">
                    {r.userCount} user{r.userCount === 1 ? '' : 's'} · {r.permissionCount}{' '}
                    permissions
                    {r.isSystem && ' · system'}
                  </div>
                </button>
              ))}
            </CardBody>
          )}
        </Card>

        <Card flush>
          <CardHeader
            title={selected ? `${selected.name} — permission matrix` : 'Permission matrix'}
            subtitle={
              selected && !selected.canManage
                ? 'This is your own role — readable so you can see what you hold, and not editable, because a role that can grant itself anything is not a limit'
                : (selected?.description ?? 'Pick a role')
            }
            actions={
              <Toolbar>
                <Select value={group} onChange={(e) => setGroup(e.target.value)}>
                  {groups.map((g) => (
                    <option key={g} value={g}>
                      {g === 'all' ? 'Every module' : g}
                    </option>
                  ))}
                </Select>
                <Button
                  size="sm"
                  variant="primary"
                  loading={busy}
                  disabled={!dirty || !caller?.canEditRole || !selected?.canManage}
                  onClick={() =>
                    selected &&
                    matrix &&
                    void run(() =>
                      apiSend<AdminWriteResult>(
                        'PATCH',
                        `/admin/roles/${encodeURIComponent(selected.code)}`,
                        { permissions: [...matrix].sort() },
                        auth(),
                      ),
                    )
                  }
                >
                  {dirty ? 'Save matrix' : 'Saved'}
                </Button>
              </Toolbar>
            }
          />
          {!selected || !matrix ? (
            <SkeletonRows rows={8} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1080px] border-collapse">
                <thead>
                  <tr className="bg-[var(--surface-2)]">
                    <Th width="46px">All</Th>
                    <Th>Module</Th>
                    {ACTIONS.map((a) => (
                      <Th key={a} align="center" width="82px">
                        {ACTION_LABEL[a] ?? a}
                      </Th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {grouped.map((g) => {
                    const whole = coverage(g.modules);
                    return (
                      <Fragment key={g.name}>
                        {}
                        <tr className="border-b border-[var(--line-2)] bg-[var(--surface-2)]">
                          <td className="px-[var(--pad)] py-1.5 text-center">
                            <TriCheckbox
                              checked={whole.all}
                              partial={whole.partial}
                              disabled={!editable}
                              label={`Every permission under ${g.name}`}
                              onChange={(on) => setAll(g.modules, on)}
                            />
                          </td>
                          <td className="px-[var(--pad)] py-1.5 text-[12.5px] font-semibold">
                            {g.name}
                            <span className="pl-2 text-[11px] font-normal text-[var(--ink-3)]">
                              {g.modules.length} module{g.modules.length === 1 ? '' : 's'}
                            </span>
                          </td>
                          {ACTIONS.map((a) => {
                            const col = coverage(g.modules, a);
                            return (
                              <td key={a} className="px-2.5 py-1.5 text-center">
                                {col.total > 0 ? (
                                  <TriCheckbox
                                    checked={col.all}
                                    partial={col.partial}
                                    disabled={!editable}
                                    label={`${ACTION_LABEL[a] ?? a} on every ${g.name} module`}
                                    onChange={(on) => setAll(g.modules, on, a)}
                                  />
                                ) : (
                                  <span className="text-[var(--ink-3)]">·</span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                        {g.modules.map((m) => {
                          const row = coverage([m]);
                          return (
                            <tr key={m.code} className="bb-row-h border-b border-[var(--line-2)]">
                              <td className="px-[var(--pad)] text-center">
                                <TriCheckbox
                                  checked={row.all}
                                  partial={row.partial}
                                  disabled={!editable}
                                  label={`Every permission on ${m.name}`}
                                  onChange={(on) => setAll([m], on)}
                                />
                              </td>
                              {}
                              <td className="pl-7 text-[12.5px] text-[var(--ink-2)]">{m.name}</td>
                              {ACTIONS.map((a) => {
                                const seesIt = matrix.has(`${m.code}:VIEW`);
                                const applies =
                                  m.allowedActions.includes(a) && (a === 'VIEW' || seesIt);
                                const held = matrix.has(`${m.code}:${a}`);
                                return (
                                  <td key={a} className="px-2.5 text-center">
                                    {applies ? (
                                      <input
                                        type="checkbox"
                                        checked={held}
                                        disabled={!editable}
                                        aria-label={`${ACTION_LABEL[a] ?? a} on ${m.name}`}
                                        title={`${ACTION_LABEL[a] ?? a} on ${m.name}`}
                                        onChange={() => toggle(m.code, a)}
                                        className="size-3.5 accent-[var(--accent)]"
                                      />
                                    ) : (
                                      <span className="text-[var(--ink-3)]">·</span>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {data?.hiddenModulesExist && (
            <CardBody className="border-t border-[var(--line-2)]">
              {}
              <Callout tone="info" title="This matrix is what your own role holds">
                A permission you do not hold is one you cannot grant — the server refuses it on Save
                — so it is not drawn as a box whose only outcome is a refusal.
              </Callout>
            </CardBody>
          )}
          {selected && (
            <CardBody className="border-t border-[var(--line-2)]">
              <div className="grid gap-3 sm:grid-cols-4">
                <Fact label="Rank" value={String(selected.rank)} mono />
                <Fact
                  label="Approval limit"
                  value={
                    selected.approvalLimitBase === null
                      ? 'Unlimited'
                      : formatMoney(selected.approvalLimitBase, { decimals: 0 })
                  }
                  mono
                />
                <Fact
                  label="Inherits subordinate scope"
                  value={selected.inheritsSubordinateScope ? 'Yes' : 'No'}
                />
                <Fact label="May approve own work" value={selected.canSelfApprove ? 'Yes' : 'No'} />
              </div>
            </CardBody>
          )}
        </Card>
      </div>
    </>
  );
}

interface RoleDraft {
  code: string;
  name: string;
  description: string;
  rank: string;
  inheritsSubordinateScope: boolean;
  canSelfApprove: boolean;
  approvalLimitBase: string;
}

function blankRole(roles: AdminRole[], callerRank: number): RoleDraft {
  const lowest = roles.reduce((max, r) => Math.max(max, r.rank), callerRank);
  return {
    code: '',
    name: '',
    description: '',
    rank: String(Math.min(999, lowest + 10)),
    inheritsSubordinateScope: false,
    canSelfApprove: false,
    approvalLimitBase: '',
  };
}

function RoleForm({
  draft,
  callerRank,
  callerRoleName,
  takenCodes,
  busy,
  onChange,
  onSave,
  onCancel,
}: {
  draft: RoleDraft;
  callerRank: number;
  callerRoleName: string;
  takenCodes: string[];
  busy: boolean;
  onChange: (draft: RoleDraft) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  const set = (patch: Partial<RoleDraft>) => onChange({ ...draft, ...patch });

  const code = draft.code.trim().toUpperCase();
  const rank = Number(draft.rank);
  const rankValid = Number.isInteger(rank) && rank >= 1 && rank <= 999;
  const codeTaken = takenCodes.includes(code);
  const outranked = rankValid && rank > callerRank;
  const ready = code !== '' && draft.name.trim() !== '' && rankValid && outranked && !codeTaken;

  return (
    <Card flush className="mb-3.5" data-noprint="1">
      <CardHeader
        title="New role"
        subtitle="Created with no permissions — tick the matrix once it exists"
        actions={
          <Toolbar>
            <Button size="sm" onClick={onCancel}>
              Cancel
            </Button>
            <Button size="sm" variant="primary" loading={busy} disabled={!ready} onClick={onSave}>
              Create
            </Button>
          </Toolbar>
        }
      />
      <CardBody className="flex flex-col gap-3.5">
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Field
            label="Code"
            required
            hint={
              codeTaken ? `${code} already exists` : 'Upper case, referenced by nothing in code'
            }
          >
            <Input
              value={draft.code}
              onChange={(e) => set({ code: e.target.value.toUpperCase() })}
              placeholder="CASHIER"
            />
          </Field>
          <Field label="Name" required hint="What the directory shows">
            <Input
              value={draft.name}
              onChange={(e) => set({ name: e.target.value })}
              placeholder="Cashier"
            />
          </Field>
          <Field
            label="Authority rank"
            required
            hint={
              !rankValid
                ? '1 to 999'
                : !outranked
                  ? `Must be below ${callerRoleName || 'your own role'} at ${callerRank}`
                  : 'Lower is more authority'
            }
          >
            <Input
              type="number"
              min={Math.max(1, callerRank + 1)}
              max={999}
              value={draft.rank}
              onChange={(e) => set({ rank: e.target.value })}
            />
          </Field>
          <Field label="Approval limit" hint="Blank is unlimited, in base currency">
            <Input
              value={draft.approvalLimitBase}
              onChange={(e) => set({ approvalLimitBase: e.target.value })}
              placeholder="—"
            />
          </Field>
        </div>

        <Field label="Description" hint="Read on the permission matrix header">
          <Input
            value={draft.description}
            onChange={(e) => set({ description: e.target.value })}
            placeholder="What this role is for"
          />
        </Field>

        <div className="grid gap-3 sm:grid-cols-2">
          <Checkbox
            checked={draft.inheritsSubordinateScope}
            onChange={(e) => set({ inheritsSubordinateScope: e.target.checked })}
            label="Inherits subordinate scope"
            description="Reaches everything beneath this role in the zone tree, not only its own scope"
          />
          <Checkbox
            checked={draft.canSelfApprove}
            onChange={(e) => set({ canSelfApprove: e.target.checked })}
            label="May approve own work"
            description="Off means somebody else has to approve what this role enters"
          />
        </div>

        <Callout tone="info" title="The permissions come next, not now">
          The role is created holding nothing, and absence of a grant denies — so it can be assigned
          to nobody usefully until the matrix is ticked and saved. That is the safe order: a role
          that exists with no permissions grants no access, whereas one created from a half-filled
          form might. You can only tick what your own role holds; the server refuses the rest.
        </Callout>
      </CardBody>
    </Card>
  );
}

const ACTIONS = [
  'VIEW',
  'CREATE',
  'EDIT',
  'DELETE',
  'POST',
  'APPROVE',
  'REVERSE',
  'PRINT',
  'EXPORT',
  'IMPORT',
  'RECONCILE',
  'CLOSE_PERIOD',
  'MANAGE',
] as const;

const ACTION_LABEL: Record<string, string> = {
  VIEW: 'View',
  CREATE: 'Create',
  EDIT: 'Edit',
  DELETE: 'Delete',
  POST: 'Post',
  APPROVE: 'Approve',
  REVERSE: 'Reverse',
  PRINT: 'Print',
  EXPORT: 'Export',
  IMPORT: 'Import',
  RECONCILE: 'Reconcile',
  CLOSE_PERIOD: 'Close period',
  MANAGE: 'Manage',
};

function TriCheckbox({
  checked,
  partial,
  disabled,
  label,
  onChange,
}: {
  checked: boolean;
  partial: boolean;
  disabled?: boolean;
  label: string;
  onChange: (next: boolean) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = partial && !checked;
  }, [partial, checked]);

  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      disabled={disabled}
      aria-label={label}
      title={label}
      onChange={() => onChange(!checked)}
      className="size-3.5 accent-[var(--accent)]"
    />
  );
}



interface ZoneDraft {
  mode: 'create' | 'edit';
  code: string;
  name: string;
  parentCode: string;
  managerEmail: string;
  isActive: boolean;
}

interface CityDraft {
  mode: 'create' | 'edit';
  code: string;
  name: string;
  province: string;
  zoneCode: string;
  isActive: boolean;
}

interface WarehouseDraft {
  mode: 'create' | 'edit';
  code: string;
  name: string;
  kind: string;
  branchCode: string;
  city: string;
  addressLine1: string;
  phone: string;
  allowNegativeStock: boolean;
  isActive: boolean;
}

const ZONE_LEVEL = ['Zone', 'Region', 'Territory'];

const WAREHOUSE_KIND_LABEL: Record<string, string> = {
  MAIN: 'Main store',
  GODOWN: 'Godown',
  TRANSIT: 'Port / in transit',
  DAMAGED: 'Damaged goods',
  BONDED: 'Bonded',
};

function ZonesScreen() {
  const [includeInactive, setIncludeInactive] = useState(false);
  const zonesRes = useResource<{ zones: AdminZone[] }>('/admin/zones', {
    ...auth(),
    include: includeInactive ? 'inactive' : undefined,
  });
  const geoRes = useResource<GeographyRefs>('/admin/geography', auth());
  const refsRes = useResource<AdminRefs>('/admin/refs', auth());
  const zones = useMemo(() => zonesRes.data?.zones ?? [], [zonesRes.data]);
  const geo = geoRes.data;
  const refs = refsRes.data;
  const caller = refs?.caller;

  const reloadAll = () => {
    void zonesRes.reload();
    void geoRes.reload();
  };
  const { busy, notice, problem, run } = useAction(reloadAll);

  const [zoneDraft, setZoneDraft] = useState<ZoneDraft | null>(null);
  const [cityDraft, setCityDraft] = useState<CityDraft | null>(null);
  const [warehouseDraft, setWarehouseDraft] = useState<WarehouseDraft | null>(null);

  const [zonePick, setZonePick] = useState<string | null>(null);

  const zone = zones.find((z) => z.code === zonePick);
  const cities = useMemo(() => geo?.cities ?? [], [geo]);
  const warehouses = useMemo(() => geo?.warehouses ?? [], [geo]);

  useEffect(() => {
    if (zonePick && !zones.some((z) => z.code === zonePick)) setZonePick(null);
  }, [zones, zonePick]);

  const inScope = useMemo(() => {
    if (!zone) return null;
    return new Set(
      zones
        .filter((z) => z.path === zone.path || z.path.startsWith(`${zone.path}.`))
        .map((z) => z.code),
    );
  }, [zone, zones]);

  const shownCities = useMemo(
    () => (inScope ? cities.filter((c) => c.zoneCode !== null && inScope.has(c.zoneCode)) : cities),
    [cities, inScope],
  );
  const shownWarehouses = warehouses;


  const saveZone = (d: ZoneDraft) =>
    void run(async () => {
      const result =
        d.mode === 'create'
          ?
            await apiSend<AdminWriteResult>(
              'POST',
              '/admin/zones',
              {
                name: d.name,
                parentCode: d.parentCode || null,
                managerEmail: d.managerEmail || null,
              },
              auth(),
            )
          : await apiSend<AdminWriteResult>(
              'PATCH',
              `/admin/zones/${encodeURIComponent(d.code)}`,
              {
                name: d.name,
                parentCode: d.parentCode || null,
                managerEmail: d.managerEmail || null,
                isActive: d.isActive,
              },
              auth(),
            );
      setZoneDraft(null);
      return result;
    });

  const saveCity = (d: CityDraft) =>
    void run(async () => {
      const payload = {
        name: d.name,
        province: d.province || null,
        zoneCode: d.zoneCode || null,
      };
      const result =
        d.mode === 'create'
          ? await apiSend<AdminWriteResult>('POST', '/admin/cities', payload, auth())
          : await apiSend<AdminWriteResult>(
              'PATCH',
              `/admin/cities/${encodeURIComponent(d.code)}`,
              { ...payload, isActive: d.isActive },
              auth(),
            );
      setCityDraft(null);
      return result;
    });

  const saveWarehouse = (d: WarehouseDraft) =>
    void run(async () => {
      const payload = {
        name: d.name,
        kind: d.kind,
        city: d.city || null,
        branchCode: d.branchCode || null,
        addressLine1: d.addressLine1 || null,
        phone: d.phone || null,
        allowNegativeStock: d.allowNegativeStock,
      };
      const result =
        d.mode === 'create'
          ? await apiSend<AdminWriteResult>('POST', '/admin/warehouses', payload, auth())
          : await apiSend<AdminWriteResult>(
              'PATCH',
              `/admin/warehouses/${encodeURIComponent(d.code)}`,
              { ...payload, isActive: d.isActive },
              auth(),
            );
      setWarehouseDraft(null);
      return result;
    });

  const remove = (kind: 'zones' | 'cities' | 'warehouses', code: string) =>
    void run(() =>
      apiSend<AdminWriteResult>(
        'DELETE',
        `/admin/${kind}/${encodeURIComponent(code)}`,
        undefined,
        auth(),
      ),
    );

  const loading = zonesRes.loading || geoRes.loading;
  const anyDraft = zoneDraft !== null || cityDraft !== null || warehouseDraft !== null;

  return (
    <>
      <PageHeader
        breadcrumb="Users & Zones / Zone Management"
        title="Zone management"
        description="The zones, cities and warehouses the business works across."
      />

      <Messages notice={notice} problem={problem} />

      <Tiles>
        <Card>
          <StatTile
            label="Zones on the map"
            value={String(zones.length)}
            hint={`${zones.filter((z) => z.depth === 0).length} top-level`}
          />
        </Card>
        <Card>
          <StatTile
            label="Cities"
            value={String(cities.length)}
            hint={
              cities.filter((c) => c.zoneCode === null).length > 0
                ? `${cities.filter((c) => c.zoneCode === null).length} in no zone`
                : 'all mapped to a zone'
            }
          />
        </Card>
        <Card>
          <StatTile
            label="Warehouses"
            value={String(warehouses.length)}
            hint={
              warehouses.filter((w) => w.inventoryAccount === null).length > 0
                ? `${warehouses.filter((w) => w.inventoryAccount === null).length} with no account`
                : 'each on its own account'
            }
          />
        </Card>
        <Card>
          <StatTile
            label="Unmanaged zones"
            value={String(zones.filter((z) => !z.managerId).length)}
            hint="no manager assigned"
          />
        </Card>
      </Tiles>

      {zoneDraft && refs && (
        <ZoneForm
          draft={zoneDraft}
          refs={refs}
          zones={zones}
          busy={busy}
          onChange={setZoneDraft}
          onSave={() => saveZone(zoneDraft)}
          onCancel={() => setZoneDraft(null)}
        />
      )}
      {cityDraft && (
        <CityForm
          draft={cityDraft}
          zones={zones}
          busy={busy}
          onChange={setCityDraft}
          onSave={() => saveCity(cityDraft)}
          onCancel={() => setCityDraft(null)}
        />
      )}
      {warehouseDraft && geo && (
        <WarehouseForm
          draft={warehouseDraft}
          geo={geo}
          busy={busy}
          onChange={setWarehouseDraft}
          onSave={() => saveWarehouse(warehouseDraft)}
          onCancel={() => setWarehouseDraft(null)}
        />
      )}

      {}
      <Card flush>
        <CardHeader
          title="Zone tree"
          subtitle="Counts are over the whole subtree — what a grant on that node actually reaches. Select a zone to narrow the two registers below it."
          actions={
            <Toolbar>
              <Button
                size="sm"
                variant={includeInactive ? 'secondary' : 'ghost'}
                onClick={() => setIncludeInactive((v) => !v)}
              >
                Show inactive
              </Button>
              <Button
                size="sm"
                variant="primary"
                disabled={!caller?.canCreateZone || !refs || anyDraft}
                onClick={() =>
                  setZoneDraft({
                    mode: 'create',
                    code: '',
                    name: '',
                    parentCode: zonePick ?? '',
                    managerEmail: '',
                    isActive: true,
                  })
                }
              >
                New zone
              </Button>
            </Toolbar>
          }
        />
        {zonesRes.loading ? (
          <SkeletonRows rows={6} />
        ) : zonesRes.error ? (
          <CardBody>
            <Callout tone="danger" title="That did not load">
              {zonesRes.error}
            </Callout>
          </CardBody>
        ) : zones.length === 0 ? (
          <EmptyState title="No zones yet" description="The map starts with a top-level zone." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] border-collapse">
              <thead>
                <tr className="bg-[var(--surface-2)]">
                  <Th>Zone</Th>
                  <Th width="180px">Manager</Th>
                  <Th align="right" width="80px">
                    Users
                  </Th>
                  <Th align="right" width="100px">
                    Customers
                  </Th>
                  <Th width="260px">Cities</Th>
                  <Th align="right" width="180px">
                    Level
                  </Th>
                </tr>
              </thead>
              <tbody>
                {zones.map((z) => (
                  <tr
                    key={z.code}
                    onClick={() => setZonePick(z.code === zonePick ? null : z.code)}
                    className={`bb-row-h cursor-pointer border-b border-[var(--line-2)] hover:bg-[var(--surface-2)] ${
                      z.code === zonePick ? 'bg-[var(--surface-2)]' : ''
                    }`}
                  >
                    <td className="px-[var(--pad)] text-[13px]">
                      {}
                      <span
                        style={{ paddingLeft: z.depth * 20 }}
                        className="inline-flex items-center gap-2"
                      >
                        <span className="bb-mono pr-1 text-[var(--ink-3)]">{z.code}</span>
                        <span className={z.depth === 0 ? 'font-semibold' : 'font-medium'}>
                          {z.name}
                        </span>
                        {!z.isActive && <Badge tone="mute">Inactive</Badge>}
                      </span>
                    </td>
                    <td className="px-2.5 text-[12.5px] text-[var(--ink-2)]">
                      {z.managerName ?? <span className="text-[var(--ink-3)]">Unassigned</span>}
                    </td>
                    <td className="bb-num px-2.5 text-right text-[12.5px]">{z.userCount}</td>
                    <td className="bb-num px-2.5 text-right text-[12.5px]">{z.customerCount}</td>
                    <td className="px-2.5 text-[11.5px] text-[var(--ink-2)]">
                      {z.cities.length > 0 ? z.cities.join(', ') : '—'}
                    </td>
                    <td className="px-[var(--pad)] text-right text-[12px] text-[var(--ink-3)]">
                      <span className="inline-flex items-center gap-2">
                        {ZONE_LEVEL[z.depth] ?? `Level ${z.level}`}
                        {z.code === zonePick && (
                          <>
                            <Button
                              size="sm"
                              disabled={!caller?.canEditZone || anyDraft}
                              onClick={(e) => {
                                e.stopPropagation();
                                setZoneDraft({
                                  mode: 'edit',
                                  code: z.code,
                                  name: z.name,
                                  parentCode: z.parentCode ?? '',
                                  managerEmail: '',
                                  isActive: z.isActive,
                                });
                              }}
                            >
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="danger"
                              loading={busy}
                              disabled={!caller?.canDeleteZone}
                              onClick={(e) => {
                                e.stopPropagation();
                                remove('zones', z.code);
                              }}
                            >
                              Delete
                            </Button>
                          </>
                        )}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {}
      <Card flush className="mt-3.5">
        <CardHeader
          title="Cities"
          subtitle={
            zone
              ? `In ${zone.code} ${zone.name} and everything under it — ${shownCities.length} of ${cities.length}`
              : 'Every city on the map. A city carries no account of its own; the warehouses under it do.'
          }
          actions={
            <Toolbar>
              {zone && (
                <Button size="sm" variant="ghost" onClick={() => setZonePick(null)}>
                  Show all zones
                </Button>
              )}
              <Button
                size="sm"
                variant="primary"
                disabled={!geo?.can.city.create || anyDraft}
                onClick={() =>
                  setCityDraft({
                    mode: 'create',
                    code: '',
                    name: '',
                    province: '',
                    zoneCode: zonePick ?? '',
                    isActive: true,
                  })
                }
              >
                New city
              </Button>
            </Toolbar>
          }
        />
        {loading ? (
          <SkeletonRows rows={4} />
        ) : geoRes.error ? (
          <CardBody>
            <Callout tone="danger" title="That did not load">
              {geoRes.error}
            </Callout>
          </CardBody>
        ) : shownCities.length === 0 ? (
          <EmptyState
            title={zone ? `No city in ${zone.name} yet` : 'No cities yet'}
            description="A city is where stock sits. Add one, then open a warehouse in it."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] border-collapse">
              <thead>
                <tr className="bg-[var(--surface-2)]">
                  <Th width="120px">Code</Th>
                  <Th>City</Th>
                  <Th width="150px">Province</Th>
                  <Th width="220px">Zone</Th>
                  <Th align="right" width="110px">
                    Warehouses
                  </Th>
                  <Th align="right" width="170px">
                    {''}
                  </Th>
                </tr>
              </thead>
              <tbody>
                {shownCities.map((c) => (
                  <tr key={c.code} className="bb-row-h border-b border-[var(--line-2)]">
                    <td className="bb-mono px-[var(--pad)] text-[12.5px] text-[var(--ink-3)]">
                      {c.code}
                    </td>
                    <td className="px-2.5 text-[13px] font-medium">
                      {c.name}
                      {!c.isActive && (
                        <Badge tone="mute" className="ml-2">
                          Inactive
                        </Badge>
                      )}
                    </td>
                    <td className="px-2.5 text-[12.5px] text-[var(--ink-2)]">
                      {c.province ?? '—'}
                    </td>
                    <td className="px-2.5 text-[12.5px] text-[var(--ink-2)]">
                      {c.zoneCode ? (
                        `${c.zoneCode} · ${c.zoneName}`
                      ) : (
                        <span className="text-[var(--warn)]">In no zone</span>
                      )}
                    </td>
                    <td className="bb-num px-2.5 text-right text-[12.5px]">{c.invoiceCount}</td>
                    <td className="px-[var(--pad)] text-right" data-noprint="1">
                      <Toolbar>
                        <Button
                          size="sm"
                          disabled={!geo?.can.city.edit || anyDraft}
                          onClick={() => {
                            setCityDraft({
                              mode: 'edit',
                              code: c.code,
                              name: c.name,
                              province: c.province ?? '',
                              zoneCode: c.zoneCode ?? '',
                              isActive: c.isActive,
                            });
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          loading={busy}
                          disabled={!geo?.can.city.delete || c.invoiceCount > 0}
                          onClick={() => remove('cities', c.code)}
                        >
                          Delete
                        </Button>
                      </Toolbar>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {}
      {geo?.can.warehouse.view !== false && (
        <Card flush className="mt-3.5">
          <CardHeader
            title="Warehouses"
            subtitle="Independent of the map above — a warehouse is a building, and every one holds its stock on its own account. Which zone and city a sale belongs to is chosen on the invoice."
            actions={
              <Toolbar>
                <Button
                  size="sm"
                  variant="primary"
                  disabled={!geo?.can.warehouse.create || !geo || anyDraft}
                  onClick={() =>
                    setWarehouseDraft({
                      mode: 'create',
                      code: '',
                      name: '',
                      kind: 'MAIN',
                      branchCode: geo?.branches.find((b) => b.isHeadOffice)?.code ?? '',
                      city: '',
                      addressLine1: '',
                      phone: '',
                      allowNegativeStock: false,
                      isActive: true,
                    })
                  }
                >
                  New warehouse
                </Button>
              </Toolbar>
            }
          />
          {loading ? (
            <SkeletonRows rows={4} />
          ) : shownWarehouses.length === 0 ? (
            <EmptyState
              title="No warehouse yet"
              description={
                geo?.stockHead
                  ? `Creating one opens its own account under ${geo.stockHead.code} ${geo.stockHead.name}.`
                  : 'This chart carries no stock account to open one under.'
              }
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[980px] border-collapse">
                <thead>
                  <tr className="bg-[var(--surface-2)]">
                    <Th width="120px">Code</Th>
                    <Th>Warehouse</Th>
                    <Th width="140px">Kind</Th>
                    <Th width="180px">Address</Th>
                    <Th width="220px">Inventory account</Th>
                    <Th align="right" width="170px">
                      {''}
                    </Th>
                  </tr>
                </thead>
                <tbody>
                  {shownWarehouses.map((w) => (
                    <tr key={w.code} className="bb-row-h border-b border-[var(--line-2)]">
                      <td className="bb-mono px-[var(--pad)] text-[12.5px] text-[var(--ink-3)]">
                        {w.code}
                      </td>
                      <td className="px-2.5 text-[13px] font-medium">
                        {w.name}
                        {w.isDefault && (
                          <Badge tone="ok" className="ml-2">
                            Default
                          </Badge>
                        )}
                        {!w.isActive && (
                          <Badge tone="mute" className="ml-2">
                            Inactive
                          </Badge>
                        )}
                      </td>
                      <td className="px-2.5 text-[12.5px] text-[var(--ink-2)]">
                        {WAREHOUSE_KIND_LABEL[w.kind] ?? w.kind}
                      </td>
                      <td className="px-2.5 text-[12.5px] text-[var(--ink-2)]">
                        {w.addressLine1 ?? '—'}
                      </td>
                      <td className="px-2.5 text-[11.5px] text-[var(--ink-2)]">
                        {w.inventoryAccount ? (
                          <span className="bb-mono">{w.inventoryAccount}</span>
                        ) : (
                          <span className="text-[var(--danger)]">no account</span>
                        )}
                        {w.inventoryAccountName && (
                          <span className="pl-2">{w.inventoryAccountName}</span>
                        )}
                      </td>
                      <td className="px-[var(--pad)] text-right" data-noprint="1">
                        <Toolbar>
                          <Button
                            size="sm"
                            disabled={!geo?.can.warehouse.edit || anyDraft}
                            onClick={() =>
                              setWarehouseDraft({
                                mode: 'edit',
                                code: w.code,
                                name: w.name,
                                kind: w.kind,
                                branchCode: w.branchCode ?? '',
                                city: '',
                                addressLine1: w.addressLine1 ?? '',
                                phone: w.phone ?? '',
                                allowNegativeStock: w.allowNegativeStock,
                                isActive: w.isActive,
                              })
                            }
                          >
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="danger"
                            loading={busy}
                            disabled={
                              !geo?.can.warehouse.delete ||
                              w.isDefault ||
                              w.movementCount > 0 ||
                              w.documentCount > 0
                            }
                            title={
                              w.movementCount > 0 || w.documentCount > 0
                                ? `${w.movementCount} stock movement(s) and ${w.documentCount} document(s) name this warehouse — deactivate it instead.`
                                : undefined
                            }
                            onClick={() => remove('warehouses', w.code)}
                          >
                            Delete
                          </Button>
                        </Toolbar>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}
    </>
  );
}

function ZoneForm({
  draft,
  refs,
  zones,
  busy,
  onChange,
  onSave,
  onCancel,
}: {
  draft: ZoneDraft;
  refs: AdminRefs;
  zones: AdminZone[];
  busy: boolean;
  onChange: (draft: ZoneDraft) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  const set = (patch: Partial<ZoneDraft>) => onChange({ ...draft, ...patch });
  const creating = draft.mode === 'create';

  const self = zones.find((z) => z.code === draft.code);
  const parents = zones.filter(
    (z) => z.depth < 2 && z.code !== draft.code && !(self && z.path.startsWith(`${self.path}.`)),
  );

  return (
    <Card flush className="mb-3.5" data-noprint="1">
      <CardHeader
        title={creating ? 'New zone' : `Edit ${draft.code}`}
        subtitle={
          creating
            ? 'The code is derived from where the zone sits — Z-01 at the top, Z-01-01 for a region under it — and comes back when it saves.'
            : 'The code is fixed: it is in the tree path every scope grant resolves through.'
        }
        actions={
          <Toolbar>
            <Button size="sm" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              size="sm"
              variant="primary"
              loading={busy}
              disabled={draft.name.trim() === ''}
              onClick={onSave}
            >
              {creating ? 'Create' : 'Save'}
            </Button>
          </Toolbar>
        }
      />
      <CardBody className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {!creating && (
          <Field label="Code" hint="Fixed — it is in the tree path">
            <Input value={draft.code} disabled />
          </Field>
        )}
        <Field label="Name" required>
          <Input value={draft.name} onChange={(e) => set({ name: e.target.value })} />
        </Field>
        <Field label="Sits under" hint="Blank makes it a top-level zone">
          <Select value={draft.parentCode} onChange={(e) => set({ parentCode: e.target.value })}>
            <option value="">— top level —</option>
            {parents.map((z) => (
              <option key={z.code} value={z.code}>
                {'— '.repeat(z.depth)}
                {z.code} · {z.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Manager" hint="Sees this subtree if their role inherits scope">
          <Select
            value={draft.managerEmail}
            onChange={(e) => set({ managerEmail: e.target.value })}
          >
            <option value="">— unassigned —</option>
            {refs.managers.map((m) => (
              <option key={m.email} value={m.email}>
                {m.fullName}
              </option>
            ))}
          </Select>
        </Field>
        {!creating && (
          <Field label="Active">
            <Checkbox
              checked={draft.isActive}
              onChange={(e) => set({ isActive: e.target.checked })}
              label="Offered on pickers and scope grants"
            />
          </Field>
        )}
      </CardBody>
    </Card>
  );
}

function CityForm({
  draft,
  zones,
  busy,
  onChange,
  onSave,
  onCancel,
}: {
  draft: CityDraft;
  zones: AdminZone[];
  busy: boolean;
  onChange: (draft: CityDraft) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  const set = (patch: Partial<CityDraft>) => onChange({ ...draft, ...patch });
  const creating = draft.mode === 'create';

  return (
    <Card flush className="mb-3.5" data-noprint="1">
      <CardHeader
        title={creating ? 'New city' : `Edit ${draft.code}`}
        subtitle="A city groups warehouses under one zone. It opens no account: the warehouses under it hold the stock, and each one has its own."
        actions={
          <Toolbar>
            <Button size="sm" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              size="sm"
              variant="primary"
              loading={busy}
              disabled={draft.name.trim() === ''}
              onClick={onSave}
            >
              {creating ? 'Create' : 'Save'}
            </Button>
          </Toolbar>
        }
      />
      <CardBody className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {!creating && (
          <Field label="Code" hint="Issued when the city was added">
            <Input value={draft.code} disabled />
          </Field>
        )}
        <Field label="City" required hint="The name has to be unique — it is what people recognise">
          <Input value={draft.name} onChange={(e) => set({ name: e.target.value })} />
        </Field>
        <Field label="Province">
          <Input value={draft.province} onChange={(e) => set({ province: e.target.value })} />
        </Field>
        <Field
          label="Zone"
          hint="Warehouses here report in this zone — moving the city moves them with it"
        >
          <Select value={draft.zoneCode} onChange={(e) => set({ zoneCode: e.target.value })}>
            <option value="">— no zone yet —</option>
            {zones.map((z) => (
              <option key={z.code} value={z.code}>
                {'— '.repeat(z.depth)}
                {z.code} · {z.name}
              </option>
            ))}
          </Select>
        </Field>
        {!creating && (
          <Field label="Active">
            <Checkbox
              checked={draft.isActive}
              onChange={(e) => set({ isActive: e.target.checked })}
              label="Offered when filing a warehouse"
            />
          </Field>
        )}
      </CardBody>
    </Card>
  );
}

function WarehouseForm({
  draft,
  geo,
  busy,
  onChange,
  onSave,
  onCancel,
}: {
  draft: WarehouseDraft;
  geo: GeographyRefs;
  busy: boolean;
  onChange: (draft: WarehouseDraft) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  const set = (patch: Partial<WarehouseDraft>) => onChange({ ...draft, ...patch });
  const creating = draft.mode === 'create';
  const existing = geo.warehouses.find((w) => w.code === draft.code);

  return (
    <Card flush className="mb-3.5" data-noprint="1">
      <CardHeader
        title={creating ? 'New warehouse' : `Edit ${draft.code}`}
        subtitle="A warehouse holds stock, so it holds an account. It sits in no zone and no city — a sale names those."
        actions={
          <Toolbar>
            <Button size="sm" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              size="sm"
              variant="primary"
              loading={busy}
              disabled={draft.name.trim() === '' || (creating && geo.stockHead === null)}
              onClick={onSave}
            >
              {creating ? 'Create' : 'Save'}
            </Button>
          </Toolbar>
        }
      />
      <CardBody>
        {}
        {creating ? (
          geo.stockHead ? (
            <Callout tone="info" title="This opens an account">
              <span className="text-[12.5px]">
                <b>
                  {geo.stockHead.code} {geo.stockHead.name}
                </b>{' '}
                gains a child named <b>Stock in Warehouse — {draft.name || '…'}</b>, and every
                receipt and issue here posts to it. The city and the zone open nothing — neither
                holds a balance of its own.
              </span>
            </Callout>
          ) : (
            <Callout tone="danger" title="There is no head to open it under">
              <span className="text-[12.5px]">
                This chart carries no stock account, so a warehouse would have nowhere to post.
                Install the standard chart, or point <b>STOCK_MAIN</b> at an inventory account under
                Setup → Link Accounts.
              </span>
            </Callout>
          )
        ) : (
          existing?.inventoryAccount && (
            <Callout tone="info" title="Its account">
              <span className="text-[12.5px]">
                <b>{existing.inventoryAccount}</b> {existing.inventoryAccountName}. Renaming the
                warehouse renames it; the code never moves, because a posted account cannot be
                re-coded.
              </span>
            </Callout>
          )
        )}

        <div className="grid gap-3 pt-3.5 sm:grid-cols-2 xl:grid-cols-4">
          {!creating && (
            <Field label="Code" hint="Issued when the warehouse was opened">
              <Input value={draft.code} disabled />
            </Field>
          )}
          <Field label="Name" required>
            <Input value={draft.name} onChange={(e) => set({ name: e.target.value })} />
          </Field>
          <Field label="Kind" hint="What the place is — it does not change where it posts">
            <Select value={draft.kind} onChange={(e) => set({ kind: e.target.value })}>
              {geo.warehouseKinds.map((k) => (
                <option key={k} value={k}>
                  {WAREHOUSE_KIND_LABEL[k] ?? k}
                </option>
              ))}
            </Select>
          </Field>
          {}
          <Field label="City" hint="Part of the address — a warehouse is not filed under a city">
            <Input value={draft.city} onChange={(e) => set({ city: e.target.value })} />
          </Field>
          <Field label="Branch">
            <Select value={draft.branchCode} onChange={(e) => set({ branchCode: e.target.value })}>
              <option value="">— none —</option>
              {geo.branches.map((b) => (
                <option key={b.code} value={b.code}>
                  {b.code} · {b.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Address">
            <Input
              value={draft.addressLine1}
              onChange={(e) => set({ addressLine1: e.target.value })}
            />
          </Field>
          <Field label="Phone">
            <Input value={draft.phone} onChange={(e) => set({ phone: e.target.value })} />
          </Field>
          <Field label="Stock rules">
            <div className="flex flex-col gap-1.5 pt-1">
              <Checkbox
                checked={draft.allowNegativeStock}
                onChange={(e) => set({ allowNegativeStock: e.target.checked })}
                label="Allow negative stock — issues may run the balance below zero"
              />
              {!creating && (
                <Checkbox
                  checked={draft.isActive}
                  onChange={(e) => set({ isActive: e.target.checked })}
                  label="Offered on entry screens"
                />
              )}
            </div>
          </Field>
        </div>
      </CardBody>
    </Card>
  );
}


function Fact({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div className="bb-eyebrow pb-1">{label}</div>
      <div className={`text-[13px] font-medium ${mono ? 'bb-num' : ''}`}>{value}</div>
    </div>
  );
}
